import {useApolloClient} from "@apollo/client";
import {message} from "antd";
import {Moment} from "moment";
import {ArrayDataOptions, SortOrder} from "../../../../generated/graphql";
import {PAYMENT_EXPORT_CONFIG, calculateChunkCount, calculateProgress, shouldUseChunkedExport} from "../utils/exportConfig";
import {CsvRowData, ExportConfig} from "../types";
import {useExportPaymentsState} from "./useExportPaymentsState";

type UseExportPaymentsLogicProps = {
    options?: ArrayDataOptions | null;
    dateRange: {start: Moment; end: Moment};
    respectFilters: boolean;
    exportConfig: ExportConfig;
};

export const useExportPaymentsLogic = ({options, dateRange, respectFilters, exportConfig}: UseExportPaymentsLogicProps) => {
    const apolloClient = useApolloClient();
    const exportStateHook = useExportPaymentsState(exportConfig.entityLabel);

    const sortField = exportConfig.sortField || exportConfig.dateFieldKey;

    const createExportOptions = (offset?: number, limit?: number): ArrayDataOptions => {
        // Den Tabellenfilter auf dieselbe Datumsspalte ersetzt der gewählte Zeitraum (doppelter Filter = doppelter Join).
        const baseFilters = respectFilters && options && options.filter ? options.filter.filter((f) => f.column !== exportConfig.dateFieldKey) : [];

        const dateFilter = {
            column: exportConfig.dateFieldKey,
            // Untergrenze = lokaler Tagesanfang, Obergrenze = lokales TagesENDE (nicht bloss "YYYY-MM-DD"):
            // Der Server vergleicht inklusiv (column <= endDate). Ein bloss als Kalendertag gesendetes Enddatum
            // wird zu 00:00 Uhr gecastet, wodurch ALLE am letzten Tag erfassten Zahlungen aus Export UND Zähler
            // fielen. Gleiche Serialisierung wie Offene-Posten-Liste / RefundsList.
            values: [dateRange.start.clone().startOf("day").toISOString(), dateRange.end.clone().endOf("day").toISOString()],
            table: exportConfig.dateFieldTable,
        };

        const effectiveLimit = limit !== undefined ? limit : PAYMENT_EXPORT_CONFIG.MAX_EXPORT_LIMIT;
        const effectiveOffset = offset !== undefined ? offset : 0;

        const shouldSortByDateField = !respectFilters || !options || !options.sortColumn;
        const sortColumn = shouldSortByDateField ? sortField : options?.sortColumn;
        const sortOrder = shouldSortByDateField ? SortOrder.Ascending : options?.sortOrder;

        return {
            filter: [...baseFilters, dateFilter],
            sortColumn: sortColumn,
            sortOrder: sortOrder,
            offset: effectiveOffset,
            limit: effectiveLimit,
        };
    };

    /**
     * Führt einen einzelnen Chunk-Export durch
     */
    const executeChunkExport = async (chunkIndex: number, totalCount: number): Promise<Array<CsvRowData>> => {
        const offset = chunkIndex * PAYMENT_EXPORT_CONFIG.CHUNK_SIZE;
        const limit = PAYMENT_EXPORT_CONFIG.CHUNK_SIZE;

        const chunkOptions = createExportOptions(offset, limit);

        const result = await apolloClient.query({
            query: exportConfig.query,
            variables: exportConfig.buildVariables(chunkOptions),
            fetchPolicy: "no-cache",
        });

        if (!result || !result.data) {
            throw new Error(`Chunk ${chunkIndex + 1}: Keine Daten erhalten`);
        }

        const extracted = exportConfig.getItemsFromResult(result.data);
        if (!extracted.items || extracted.items.length === 0) {
            throw new Error(`Chunk ${chunkIndex + 1}: Keine Daten erhalten`);
        }

        const progress = calculateProgress(chunkIndex, totalCount);
        exportStateHook.updateProgress(progress);

        return extracted.items as any;
    };

    /**
     * Chunked Export - für große Datenmengen
     */
    const handleChunkedExport = async (totalCount: number) => {
        try {
            const fileName = `${exportConfig.fileNamePrefix}-${dateRange.start.format("YYYY-MM-DD")}-${dateRange.end.format("YYYY-MM-DD")}.csv`;

            exportStateHook.showExportModal(fileName, totalCount);

            const allCsvData: Array<CsvRowData> = [];
            const chunks = calculateChunkCount(totalCount);

            for (let chunkIndex = 0; chunkIndex < chunks; chunkIndex++) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const chunkData = await executeChunkExport(chunkIndex, totalCount);

                    allCsvData.push(...chunkData);

                    exportStateHook.setExportData([...allCsvData]);

                    if (chunkIndex < chunks - 1) {
                        // eslint-disable-next-line no-await-in-loop
                        await new Promise((resolve) => setTimeout(resolve, PAYMENT_EXPORT_CONFIG.CHUNK_DELAY_MS));
                    }
                } catch (chunkError) {
                    console.error(`Fehler bei Chunk ${chunkIndex + 1}:`, chunkError);
                    message.warning({
                        content: `Warnung: Chunk ${chunkIndex + 1} konnte nicht geladen werden`,
                        key: "export-chunk-warning",
                    });
                }
            }

            exportStateHook.handleExportSuccess(allCsvData.length);
        } catch (error) {
            console.error("Chunked export error:", error);
            exportStateHook.handleExportError("Fehler beim Chunked Export");
        }
    };

    /**
     * Einfacher Export - für kleinere Datenmengen
     */
    const handleSimpleExport = async () => {
        try {
            const fileName = `${exportConfig.fileNamePrefix}-${dateRange.start.format("YYYY-MM-DD")}-${dateRange.end.format("YYYY-MM-DD")}.csv`;

            exportStateHook.showExportModal(fileName);

            const exportOptions = createExportOptions();

            const result = await apolloClient.query({
                query: exportConfig.query,
                variables: exportConfig.buildVariables(exportOptions),
                fetchPolicy: "no-cache",
            });

            if (result && result.data) {
                const extracted = exportConfig.getItemsFromResult(result.data);
                if (extracted.items && extracted.items.length > 0) {
                    exportStateHook.setExportData(extracted.items as any);
                    exportStateHook.handleExportSuccess(extracted.items.length);
                } else {
                    exportStateHook.handleExportError("Keine Daten zum Exportieren gefunden");
                }
            } else {
                exportStateHook.handleExportError("Keine Daten zum Exportieren gefunden");
            }
        } catch (error) {
            console.error("Simple export error:", error);
            exportStateHook.handleExportError("Fehler beim Export");
        }
    };

    /**
     * Hauptexport-Handler - entscheidet zwischen einfach und chunked
     */
    const handleExport = (totalCount: number) => {
        if (shouldUseChunkedExport(totalCount)) {
            handleChunkedExport(totalCount);
        } else {
            handleSimpleExport();
        }
    };

    return {
        ...exportStateHook,
        handleExport,
        exportLoading: exportStateHook.isExporting,
        shouldUseChunkedExport,
    };
};
